import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import { getState } from "../../state/store";
import { selectDarkMode } from "../../state/selectors";

import { colors } from "../../consts";
import { t } from "../../services";

import { FlexContainers, SVG, ViewOnlyInputValues, WrapperContainer } from "../../components";
import {
  IconWrapper,
  InputComponentWrapper,
  InputContainer,
  InputTitleWrapper,
  InputWrapper
} from "./styled";

const Input = ({
  id,
  title,
  value,
  setValue,
  onKeyDown,
  onBlur,
  placeholder,
  type = "text",
  maxLength,
  isPassword,
  isReadonly,
  isRequired,
  isSmall,
  showLengthCounter,
  disabled,
  hasError,
  icon,
  inputIcon,
  viewOnly,
  warningMsg,
  autoComplete,
  hideInputComponentWrapper,
  InputComponent,
  min,
  max,
  innerRef
}) => {
  const [inputType, setInputType] = useState(isPassword ? "password" : type);
  const [focused, setFocused] = useState(false);

  const valueLength = useMemo(() => {
    return value?.length ?? 0;
  }, [value]);

  const darkMode = selectDarkMode()(getState());

  return (
    <InputContainer
      data-testid="input-container"
      className={disabled ? "disabled" : hasError ? "error" : ""}>
      <WrapperContainer Container={FlexContainers.FlexRowBetween} width="100%">
        {title && (
          <InputTitleWrapper>
            <label htmlFor={id}>
              {title}
              {isRequired ? <span> *</span> : ""}
            </label>
            {icon && icon}
          </InputTitleWrapper>
        )}

        {(warningMsg || showLengthCounter) && (
          <WrapperContainer Container={FlexContainers.FlexRowEnd}>
            <InputTitleWrapper>
              <WrapperContainer width="max-content">
                <p>{warningMsg ? warningMsg : `${valueLength}/${maxLength}`}</p>
              </WrapperContainer>
            </InputTitleWrapper>
          </WrapperContainer>
        )}
      </WrapperContainer>

      {viewOnly ? (
        <ViewOnlyInputValues value={value} placeholder={placeholder} />
      ) : (
        <>
          <InputWrapper hasIcon={isPassword || inputIcon} value={value} isSmall={isSmall}>
            {InputComponent ? (
              hideInputComponentWrapper ? (
                <>{InputComponent}</>
              ) : (
                <InputComponentWrapper>{InputComponent}</InputComponentWrapper>
              )
            ) : (
              <input
                id={id}
                data-testid="input"
                type={inputType}
                value={value ? value : ""}
                placeholder={t(placeholder)}
                onChange={(e) => {
                  if (maxLength > 0) {
                    e.target.value.length <= maxLength && setValue(e.target.value);
                  } else {
                    setValue(e.target.value);
                  }
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                  setFocused(false);
                  onBlur && onBlur();
                }}
                onKeyDown={onKeyDown}
                disabled={disabled}
                readOnly={isReadonly}
                autoComplete={autoComplete ? autoComplete : "off"}
                min={min}
                max={max}
                ref={innerRef}
              />
            )}

            {isPassword && (
              <IconWrapper
                data-testid="show-password-icon"
                onClick={() => {
                  setInputType((prev) => (prev === "text" ? "password" : "text"));
                }}>
                {inputType === "text" ? (
                  <SVG.HidePasswordIcon
                    color={
                      focused && value?.length > 0
                        ? darkMode
                          ? colors.WHITE
                          : colors.RAISIN_100
                        : colors.RAISIN_50
                    }
                  />
                ) : (
                  <SVG.ShowPasswordIcon
                    color={
                      focused && value?.length > 0
                        ? darkMode
                          ? colors.WHITE
                          : colors.RAISIN_100
                        : colors.RAISIN_50
                    }
                  />
                )}
              </IconWrapper>
            )}

            {inputIcon && !isPassword && (
              <IconWrapper data-testid="input-icon">{inputIcon}</IconWrapper>
            )}
          </InputWrapper>
        </>
      )}
    </InputContainer>
  );
};

Input.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setValue: PropTypes.func,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  maxLength: PropTypes.number,
  isPassword: PropTypes.bool,
  isReadonly: PropTypes.bool,
  isRequired: PropTypes.bool,
  isSmall: PropTypes.bool,
  showLengthCounter: PropTypes.bool,
  disabled: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  icon: PropTypes.node,
  inputIcon: PropTypes.node,
  viewOnly: PropTypes.bool,
  warningMsg: PropTypes.string,
  autoComplete: PropTypes.bool,
  hideInputComponentWrapper: PropTypes.bool,
  InputComponent: PropTypes.node,
  min: PropTypes.number,
  max: PropTypes.number,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};

export default Input;
